import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Sparkles, Users, Calendar, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventPlanning = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_date: '',
    guest_count: '',
    event_type: '',
    budget_range: '',
    service_package: '',
    special_requirements: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const packages = [
    {
      name: 'Essential Package',
      icon: <Sparkles size={40} />,
      price: 'Starting at $1,500',
      features: [
        'Event coordination',
        'Venue recommendations',
        'Basic décor setup',
        'Timeline creation',
        'Vendor coordination'
      ]
    },
    {
      name: 'Premium Package',
      icon: <Users size={40} />,
      price: 'Starting at $3,500',
      features: [
        'Full-service planning',
        'Custom theme design',
        'Complete décor package',
        'Entertainment coordination',
        'Photography coordination',
        'Day-of coordination'
      ]
    },
    {
      name: 'Luxury Package',
      icon: <Award size={40} />,
      price: 'Starting at $7,000',
      features: [
        'Exclusive planning services',
        'Custom everything',
        'Premium venue selection',
        'Luxury décor & florals',
        'Full vendor management',
        'Dedicated event manager',
        'Post-event breakdown'
      ]
    }
  ];

  const galleryImages = [
    {
      title: 'Elegant Wedding Reception',
      description: 'Beautiful outdoor wedding with 200 guests',
      image: 'https://images.unsplash.com/photo-1723832348105-2e69f948135a?w=600&q=80'
    },
    {
      title: 'Corporate Gala',
      description: 'Sophisticated corporate event for 500 attendees',
      image: 'https://images.pexels.com/photos/50675/banquet-wedding-society-deco-50675.jpeg?w=600&q=80'
    },
    {
      title: 'Birthday Celebration',
      description: 'Vibrant 50th birthday party with Caribbean theme',
      image: 'https://images.unsplash.com/photo-1631857455684-a54a2f03665f?w=600&q=80'
    },
    {
      title: 'Anniversary Dinner',
      description: 'Intimate 25th anniversary celebration',
      image: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=600&q=80'
    },
    {
      title: 'Holiday Party',
      description: 'Festive company holiday celebration',
      image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&q=80'
    },
    {
      title: 'Graduation Party',
      description: 'Fun and colorful graduation celebration',
      image: 'https://images.unsplash.com/photo-1523438885200-e635ba2c371e?w=600&q=80'
    }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        guest_count: parseInt(formData.guest_count)
      };

      await axios.post(`${API}/events/quote`, submitData);
      toast.success('Quote request submitted successfully! We\'ll contact you soon.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        event_date: '',
        guest_count: '',
        event_type: '',
        budget_range: '',
        service_package: '',
        special_requirements: ''
      });
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="relative min-h-[50vh] flex items-center justify-center overflow-hidden" 
        data-testid="events-hero-section"
        style={{
          backgroundImage: 'linear-gradient(rgba(78, 172, 228, 0.85), rgba(0, 242, 254, 0.85)), url(https://images.unsplash.com/photo-1681270543584-8e541a1bb056?w=1600&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="hero-content fade-in-up">
          <h1 className="hero-title text-white drop-shadow-lg" data-testid="events-title">Event Planning</h1>
          <div className="decorative-line bg-white"></div>
          <p className="hero-subtitle text-white drop-shadow-md" data-testid="events-subtitle">
            Let us handle every detail of your special occasion
          </p>
        </div>
      </section>

      {/* Packages Section */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' }} data-testid="events-packages-section">
        <h2 className="section-title">Service Packages</h2>
        <div className="decorative-line"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
          {packages.map((pkg, index) => (
            <div
              key={index}
              className="card hover:shadow-2xl transition-all"
              data-testid={`events-package-${index}`}
            >
              <div className="text-[#d2691e] mb-4 flex justify-center">
                {pkg.icon}
              </div>
              <h3 className="text-2xl font-bold mb-2 text-[#8b4513] text-center">{pkg.name}</h3>
              <p className="text-xl text-[#d2691e] font-semibold text-center mb-4">{pkg.price}</p>
              <ul className="space-y-3 text-[#6b4423]">
                {pkg.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-[#d2691e] mr-2">✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' }} data-testid="events-gallery-section">
        <h2 className="section-title">Recent Events</h2>
        <div className="decorative-line"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {galleryImages.map((image, index) => (
            <div
              key={index}
              className="card group cursor-pointer overflow-hidden"
              data-testid={`events-gallery-item-${index}`}
            >
              <div className="aspect-video overflow-hidden rounded-lg mb-4 relative">
                <img 
                  src={image.image}
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#4facfe] to-[#00f2fe] opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
              </div>
              <h3 className="text-xl font-bold mb-2 text-[#8b4513]">{image.title}</h3>
              <p className="text-[#6b4423]">{image.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Quote Request Form */}
      <section className="section" style={{ background: 'white' }} data-testid="events-quote-section">
        <div className="max-w-3xl mx-auto">
          <h2 className="section-title">Request a Quote</h2>
          <div className="decorative-line"></div>
          <p className="text-center text-[#6b4423] mb-8">
            Tell us about your event and we'll create a custom proposal for you
          </p>

          <form onSubmit={handleSubmit} className="card" data-testid="events-quote-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  required
                  data-testid="events-form-name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  required
                  data-testid="events-form-email"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  required
                  data-testid="events-form-phone"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Event Date *</label>
                <input
                  type="date"
                  name="event_date"
                  value={formData.event_date}
                  onChange={handleChange}
                  className="form-input"
                  required
                  data-testid="events-form-date"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Number of Guests *</label>
                <input
                  type="number"
                  name="guest_count"
                  value={formData.guest_count}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                  required
                  data-testid="events-form-guests"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Event Type *</label>
                <select
                  name="event_type"
                  value={formData.event_type}
                  onChange={handleChange}
                  className="form-select"
                  required
                  data-testid="events-form-type"
                >
                  <option value="">Select event type</option>
                  <option value="wedding">Wedding</option>
                  <option value="corporate">Corporate Event</option>
                  <option value="birthday">Birthday Party</option>
                  <option value="anniversary">Anniversary</option>
                  <option value="graduation">Graduation</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Budget Range *</label>
                <select
                  name="budget_range"
                  value={formData.budget_range}
                  onChange={handleChange}
                  className="form-select"
                  required
                  data-testid="events-form-budget"
                >
                  <option value="">Select budget range</option>
                  <option value="$1,000-$2,500">$1,000 - $2,500</option>
                  <option value="$2,500-$5,000">$2,500 - $5,000</option>
                  <option value="$5,000-$10,000">$5,000 - $10,000</option>
                  <option value="$10,000+">$10,000+</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Service Package *</label>
                <select
                  name="service_package"
                  value={formData.service_package}
                  onChange={handleChange}
                  className="form-select"
                  required
                  data-testid="events-form-package"
                >
                  <option value="">Select package</option>
                  <option value="essential">Essential Package</option>
                  <option value="premium">Premium Package</option>
                  <option value="luxury">Luxury Package</option>
                  <option value="custom">Custom Package</option>
                </select>
              </div>
            </div>
            <div className="form-group mt-6">
              <label className="form-label">Special Requirements</label>
              <textarea
                name="special_requirements"
                value={formData.special_requirements}
                onChange={handleChange}
                className="form-textarea"
                placeholder="Tell us about any special requests, dietary restrictions, or other details..."
                data-testid="events-form-requirements"
              ></textarea>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full mt-6 bg-[#d2691e] hover:bg-[#c25a1a]"
              data-testid="events-form-submit"
            >
              {loading ? 'Submitting...' : 'Request Quote'}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default EventPlanning;